/**
 * navCatalog.ts — Phase 4 Task 2 (2026-06-19)
 *
 * Catalog of every Veda Agro nav item, each tagged with a `navScope` key.
 * Used by `buildScopedSections` to derive the sidebar from the user's
 * `nav.*` scopes (flag ON path). The hardcoded VEDA_*_SECTIONS arrays in
 * Sidebar.tsx remain the authoritative flag-OFF default.
 *
 * IMPORTANT: icons and labels here MUST match the hardcoded sections in
 * Sidebar.tsx so derived nav looks identical when flag is ON.
 *
 * D-09 rule: no tenant slugs or IDs.
 */

import {
  Phone, Target, FileSearch, Receipt, TrendingUp,
  Truck, Route, Workflow, PackageCheck,
  Gauge, Link2, Tags, Users, Inbox, Wrench,
  Ban, BadgeIndianRupee,
  GitCommitHorizontal,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */

type Icon = typeof Phone;

export interface CatalogNavItem {
  to: string;
  icon: Icon;
  label: string;
  /** Module key for tenant feature-flag filtering (matches NavItem.module). */
  module?: string;
  /** The nav.* scope that gates this item. */
  navScope: string;
  /** Existing role-specific menus may expose this screen through another nav grant. */
  alternateScopesByRole?: Record<string, string>;
  /**
   * Hide the item when the user ALSO holds this scope. Used where one grant
   * (e.g. nav.my_pipelines) is shared by roles that must see different menus.
   */
  hiddenWithScope?: string;
}

export interface CatalogSection {
  label: string;
  items: CatalogNavItem[];
}

// Re-export NavSection shape so buildScopedSections result is compatible with
// what Sidebar.tsx expects (NavSection has items: NavItem[], no seeMore for
// scope-derived sections — scope-gated nav is exact, no see-more drawer).
export interface NavSection {
  label: string;
  items: CatalogNavItem[];
  seeMore?: CatalogNavItem[];
}

/* ------------------------------------------------------------------ */
/*  Catalog                                                             */
/*                                                                      */
/*  Grouped by role cluster so the sections produced by                 */
/*  buildScopedSections feel natural. When a user holds scopes from     */
/*  multiple clusters, items fold into whichever section they belong to. */
/* ------------------------------------------------------------------ */

export const VEDA_NAV_CATALOG: CatalogSection[] = [
  {
    label: 'Sales',
    items: [
      {
        to: '/veda-agro/call-queue',
        icon: Phone,
        label: 'Call List',
        module: 'veda_commodity_deals',
        navScope: 'nav.call_list',
      },
      {
        to: '/veda-agro/sales',
        icon: Target,
        label: 'Trading Dashboard',
        module: 'veda_commodity_deals',
        navScope: 'nav.trading_dashboard',
      },
      {
        // Requirements (sales) routes to /veda-agro/enquiries, distinct from
        // "Deal Desk" (purchase) which also routes to /veda-agro/enquiries but
        // carries a different scope + label. Keep as separate catalog entries.
        to: '/veda-agro/enquiries',
        icon: FileSearch,
        label: 'Requirements',
        module: 'veda_commodity_deals',
        navScope: 'nav.leads',
        alternateScopesByRole: { sales_rep: 'nav.trading_dashboard' },
      },
      {
        to: '/veda-agro/orders',
        icon: Receipt,
        label: 'Sales Orders',
        module: 'veda_commodity_deals',
        navScope: 'nav.sales_orders',
      },
      {
        // Customer Master — create is open to the sales + purchase desks
        // (2026-07-03, Manak). REUSES nav.sales_orders (Freight-Desk/Mapping
        // precedent): exactly sales_rep + sales_manager + purchase_head hold
        // it; logistics does not → no scope reseed needed. Admin/owner get it
        // via the hardcoded arrays (delete lives behind a backend admin gate).
        to: '/veda-agro/customers',
        icon: Users,
        label: 'Customers',
        module: 'veda_commodity_deals',
        navScope: 'nav.sales_orders',
      },
      {
        // V6 (2026-07-16): Renegotiations dashboard — visible to sales_rep +
        // sales_manager + purchase_head. REUSES nav.sales_orders (Customers
        // precedent): exactly those three roles hold it; logistics does not →
        // no scope reseed needed, and the flag-OFF hardcoded arrays already
        // carry it in the rep + both manager navs. Admin/owner reach it via the
        // backend role-scope (managers see all) on the route itself.
        to: '/veda-agro/renegotiations',
        icon: Inbox,
        label: 'Renegotiations',
        module: 'veda_commodity_deals',
        navScope: 'nav.sales_orders',
      },
    ],
  },
  {
    label: 'Logistics',
    items: [
      {
        to: '/veda-agro/logistics',
        icon: Truck,
        label: 'Logistics Cockpit',
        module: 'veda_commodity_deals',
        navScope: 'nav.logistics_cockpit',
      },
      {
        // Dedicated Freight Desk (two-pane: pick transporter + lock ₹/kg).
        // REUSES nav.logistics_cockpit scope — anyone who sees the cockpit sees this.
        to: '/veda-agro/freight-desk',
        icon: PackageCheck,
        label: 'Freight Desk',
        module: 'veda_commodity_deals',
        navScope: 'nav.logistics_cockpit',
      },
      {
        to: '/veda-agro/trade-routes',
        icon: Route,
        label: 'Trade Routes',
        module: 'veda_commodity_deals',
        navScope: 'nav.trade_routes',
      },
      {
        // 🔒 SCOPE REUSE, NOT A NEW SCOPE — the Freight Desk / Rejected
        // Dispatches precedent. My Pipelines is the read-only, owner-scoped
        // view of the same trucks, so whoever may see the tracker may see
        // their own slice of it. A new nav.* would need a scope reseed on the
        // box before anybody could reach the page.
        /**
         * 🔒 ITS OWN SCOPE SINCE 2026-09-04. It rode `nav.stage_tracker` for one
         * afternoon, which was fine while both were visible together. Manak then
         * took the Dispatch Tracker away from sales and purchase — "my pipeline
         * is enough of a view for them" — and one shared scope cannot express
         * that: dropping the tracker would have dropped this screen with it.
         */
        to: '/veda-agro/my-pipelines',
        icon: GitCommitHorizontal,
        label: 'My Pipelines',
        module: 'veda_commodity_deals',
        navScope: 'nav.my_pipelines',
      },
      {
        to: '/veda-agro/stage-tracker',
        icon: Workflow,
        label: 'Dispatch Tracker',
        module: 'veda_commodity_deals',
        navScope: 'nav.stage_tracker',
      },
    ],
  },
  {
    label: 'Purchase',
    items: [
      {
        // "Deal Desk" (purchase) — same route as "Leads" (sales) but
        // different scope + label. Both are valid catalog entries.
        to: '/veda-agro/enquiries',
        icon: FileSearch,
        label: 'Deal Desk',
        module: 'veda_commodity_deals',
        navScope: 'nav.deal_desk',
      },
      {
        to: '/veda-agro/enquiry-inbox',
        icon: TrendingUp,
        label: 'Renegotiate',
        module: 'veda_commodity_deals',
        navScope: 'nav.deal_desk',
      },
      {
        to: '/veda-agro/cockpit',
        icon: Gauge,
        label: 'Purchase Cockpit',
        module: 'veda_commodity_deals',
        navScope: 'nav.purchase_cockpit',
      },
      {
        to: '/veda-agro/work-plan',
        icon: Link2,
        label: 'Allocation Board',
        module: 'veda_commodity_deals',
        navScope: 'nav.allocation_board',
      },
      {
        /**
         * 🔒 THE SAME BOARD ON A SECOND SCOPE, ON PURPOSE. Manak, 2026-09-04:
         * logistics must reach the Allocation Board seamlessly. Granting them
         * `nav.allocation_board` was the obvious move and the wrong one — the
         * Holding Desk deliberately rides that same scope so the people RAISING
         * charges cannot also see the desk that APPROVES them. Riding
         * `nav.logistics_cockpit` gives logistics the board and nothing else,
         * and needs no scope reseed on the box.
         */
        to: '/veda-agro/work-plan',
        icon: Link2,
        label: 'Allocation Board',
        module: 'veda_commodity_deals',
        navScope: 'nav.logistics_cockpit',
      },
      {
        // Mapping Manager — vendor↔commodity master-data for the purchase desk.
        // Reuses nav.allocation_board scope (Freight Desk precedent): anyone who
        // can run the purchase desk's allocation board can manage plant mappings.
        to: '/veda-agro/mapping',
        icon: Tags,
        label: 'Mapping',
        module: 'veda_commodity_deals',
        navScope: 'nav.allocation_board',
      },
    ],
  },
  {
    // 🔒 SCOPE REUSE, NOT A NEW SCOPE. Freight Desk and Mapping set the
    // precedent: reusing an existing navScope means no scope reseed on the
    // box. Rejected Dispatches reuses nav.stage_tracker — whoever runs the
    // tracker should see what fell out of it. The Holding Desk reuses
    // nav.allocation_board, which logistics operators do NOT hold: the people
    // raising charges must not also see the desk that approves them.
    label: 'Admin',
    items: [
      {
        /**
         * 🔒 FOLLOWS THE PIPELINE VIEW, NOT THE TRACKER (2026-09-04). The old
         * rule — "whoever runs the tracker sees what fell out of it" — would
         * have taken this away from sales and purchase along with the tracker.
         * They still need to see a load their customer refused; they simply do
         * not need the desk that moves trucks.
         */
        to: '/veda-agro/rejected-dispatches',
        icon: Ban,
        label: 'Rejected Dispatches',
        module: 'veda_commodity_deals',
        navScope: 'nav.my_pipelines',
        /**
         * 🔒 NOT FOR SALES (Manak 2026-09-21: "for navigation of sales rep,
         * admin rejected dispatches should not be a part of it"). Sales roles
         * are the only ones holding nav.call_list; purchase keeps it.
         */
        hiddenWithScope: 'nav.call_list',
      },
      {
        to: '/veda-agro/holding-desk',
        icon: BadgeIndianRupee,
        label: 'Holding Desk',
        module: 'veda_commodity_deals',
        navScope: 'nav.allocation_board',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  buildScopedSections                                                 */
/* ------------------------------------------------------------------ */

/**
 * Pure function — given a user's scopes (string[]), filter the catalog
 * to items whose navScope is in the scope set.
 *
 * - Returns a NavSection[] compatible with Sidebar.tsx's baseSections shape.
 * - Empty sections (all items filtered out) are dropped.
 * - Result is deterministic (catalog order preserved).
 *
 * Flag-OFF / empty scopes: caller should check `userScopes.length > 0`
 * before calling this and fall back to SECTIONS_BY_VIEW[view] instead.
 */
export function buildScopedSections(userScopes: string[], roleName = ''): NavSection[] {
  const scopeSet = new Set(userScopes);
  const result: NavSection[] = [];

  for (const section of VEDA_NAV_CATALOG) {
    const matchedItems = section.items.filter(
      (item) => (scopeSet.has(item.navScope) || scopeSet.has(item.alternateScopesByRole?.[roleName] ?? ''))
        && !(item.hiddenWithScope && scopeSet.has(item.hiddenWithScope)),
    );
    if (matchedItems.length > 0) {
      result.push({ label: section.label, items: matchedItems });
    }
  }

  return result;
}
